import { axiosInstance } from "@/services/axiosInstance";
import { User } from "@/models/user.model";

interface UserResponse {
  status: string;
  data: User;
}

export interface GetUsersFetchParams {
  page: number;
  pageSize: number;
  user: User;
  isCorbanRoute: boolean;
  isUsersInternPromotoraRoute: boolean;
  corbanRelatedId?: string;
}

interface UsersPaginatedResponse {
  data: User[];
  page: string;
  pageSize: string;
  status: "success" | "error";
  totalPages: number;
  isAllowedFinancialSettingsCorbanId?: boolean;
}
interface UserFilters {
  page: number;
  pageSize: number;
  roleId?: string;
  corbanId?: number;
  creditPromoterId?: number;
  ownerId?: string;
  enabled?: boolean;
  sortBy?: string;
  sortDirection?: string;
  search?: string;
}

export const getUsers = async (
  page: number,
  pageSize: number,
  corbanId?: any,
  creditPromoterId?: any,
  sortBy?: string,
  sortDirection?: string,
  search?: string
): Promise<UsersPaginatedResponse> => {
  try {
    let url = "";
    let params: any = { page, pageSize, sortBy, sortDirection, search };
    if (corbanId) {
      url = "/api/user/corban";
      params.corbanId = corbanId;
    }else  if (creditPromoterId) {
      url = "/api/user/credit-promoter";
      params.creditPromoterId = creditPromoterId;
    }
    const response = await axiosInstance.get<UsersPaginatedResponse>(url, { params });
    return response.data;
  } catch (error) {
    console.error("Erro ao obter usuários:", error);
    throw error;
  }
};

export const getUsersByCreditPromoterId = async (
  filter: UserFilters
): Promise<UsersPaginatedResponse> => {
  try {
    const response = await axiosInstance.get<UsersPaginatedResponse>(
      "/api/users/filtered",
      {
        params: filter,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erro ao obter usuários pelo ID do Credit Promoter:", error);
    throw error;
  }
};

export const getUserById = async (
  userId: string | undefined
): Promise<User | null> => {
  if (!userId) return null;
  try {
    const response = await axiosInstance.get<UserResponse>(`/api/user/get`, {
      params: {
        userId,
      },
    });
    return response.data.data;
  } catch (error) {
    console.error("Erro ao obter o usuário:", error);
    throw error;
  }
};

export const getUsersByCorbanId = async (
  corbanId: number,
  page: number,
  pageSize: number,
  sortBy: string,
  sortDirection: string,
  search: string
): Promise<any> => {
  try {
    const response = await axiosInstance.get<any>(`/api/user/corban`, {
      params: {
        corbanId,
        page,
        pageSize,
        sortBy,
        sortDirection,
        search,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erro ao obter usuários pelo Corban ID:", error);
    throw error;
  }
};

export const registerUser = async (userData: Partial<User>): Promise<User> => {
  try {
    const response = await axiosInstance.post<User>(
      "/api/auth/register",
      userData
    );
    return response.data;
  } catch (error) {
    console.error("Erro ao registrar usuário:", error);
    throw error;
  }
};

export const updateUser = async (userData: Partial<User>): Promise<User> => {
  const { corbanId, creditPromoterId, ...userWithoutCorbanAndCreditPromoter } =
    userData;
  try {
    const response = await axiosInstance.put<User>(
      `/api/user/${userData.id}`,
      userWithoutCorbanAndCreditPromoter
    );
    return response.data;
  } catch (error) {
    console.error("Erro ao atualizar usuário:", error);
    throw error;
  }
};

export const updateUserStatus = async (user: Partial<User>): Promise<User> => {
  try {
    const response = await axiosInstance.patch<User>(
      `/api/user/${user.id}/status `,
      user
    );
    return response.data;
  } catch (error) {
    console.error("Erro ao atualizar usuário:", error);
    throw error;
  }
};

export const blacklistUser = async (userId: string): Promise<User> => {
  try {
    const response = await axiosInstance.patch<User>(`/api/user/${userId}/disable`);
    return response.data;
  } catch (error) {
    console.error("Erro ao adicionar usuário à blacklist:", error);
    throw error;
  }
};

export const unblacklistUser = async (userId: string): Promise<User> => {
  try {
    const response = await axiosInstance.patch<User>(`/api/user/${userId}/enable`);
    return response.data;
  } catch (error) {
    console.error("Erro ao remover usuário da blacklist:", error);
    throw error;
  }
};

interface UpdatePasswordData {
  oldPassword: string;
  password: string;
}

export const updatePassword = async (
  data: UpdatePasswordData
): Promise<void> => {
  try {
    await axiosInstance.put("/api/user/password", {
      oldPassword: data.oldPassword,
      password: data.password,
    });
  } catch (error) {
    throw new Error("Erro ao atualizar a senha");
  }
};

export const resetPassword = async (user: User): Promise<void> => {
  try {
    await axiosInstance.put("/api/user/reset-password", {
      userId: user.id,
      password: "mudar123",
    });
  } catch (erro) {
    throw new Error("Erro ao resetar a senha");
  }
};

export async function checkExistingUsers(
  users: Partial<User>[]
): Promise<User[]> {
  const response = await axiosInstance.post("/api/users/check-existing", {
    users,
  });
  return response.data.data;
}

export async function createUsersBatch(users: Partial<User>[]): Promise<void> {
  try {
    const response = await axiosInstance.post("/api/auth/register/batch", {
      users,
    });
    return response.data;
  } catch (error) {
    console.error("Erro ao criar usuários em lote:", error);
    throw error;
  }
}

export const getOldestMasterByCorbanId = async (
  corbanId: number
): Promise<User> => {
  try {
    const response = await axiosInstance.get<UserResponse>(
      "/api/user/oldest-master",
      { params: { corbanId } }
    );

    if (response.data.status !== "success") {
      throw new Error("Failed to fetch oldest master user");
    }

    return response.data.data;
  } catch (error) {
    console.error("Erro ao obter o usuário master mais antigo:", error);
    throw error;
  }
};

export const getOldestMasterByCreditPromoterId = async (
  creditPromoterId: number
): Promise<User> => {
  try {
    const response = await axiosInstance.get<UserResponse>(
      "/api/user/credit-promoter/oldest-master",
      { params: { creditPromoterId } }
    );

    if (response.data.status !== "success") {
      throw new Error("Failed to fetch oldest master user");
    }

    return response.data.data;
  } catch (error) {
    console.error("Erro ao obter o usuário master mais antigo:", error);
    throw error;
  }
};

export const getFilteredUsers = async (
  filters: UserFilters
): Promise<UsersPaginatedResponse> => {
  try {
    const response = await axiosInstance.get<UsersPaginatedResponse>(
      "/api/users/filtered",
      {
        params: filters,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erro ao obter usuários filtrados:", error);
    throw error;
  }
};

export const getCommercialManagers = async (): Promise<User[]> => {
  try {
    const { data } = await axiosInstance.get<User[]>(
      "/api/users/commercial-manager"
    );
    if (!Array.isArray(data)) {
      return [];
    }
    return data;
  } catch (error: any) {
    console.error("Error: ", error);
    if (error.response && error.response.status === 404) {
      return [];
    }
    throw error;
  }
};

export const getCommercialManagersByName = async (
  search: string,
  page: number,
  pageSize: number
) => {
  try {
    const { data } = await axiosInstance.get(
      "/api/users/commercial-manager-name",
      { params: { search, page, pageSize } }
    );

    return data.data;
  } catch (error: any) {
    console.error("Error: ", error);
    if (error.response && error.response.status === 404) {
      return [];
    }
    throw error;
  }
};

export const getSellerByName = async (
  search: string,
  page: number,
  pageSize: number,
  corbanId?: number
) => {
  try {
    const { data } = await axiosInstance.get("/api/users/seller-name", {
      params: { search, page, pageSize, corbanId },
    });

    return data.data;
  } catch (error: any) {
    console.error("Error: ", error);
    if (error.response && error.response.status === 404) {
      return [];
    }
    throw error;
  }
};
